import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config';
import { getToken } from '../services/authService';
import toast from 'react-hot-toast';

export default function AdminMedia() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 24;
    const fileInputRef = useRef(null);

    const fetchFiles = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/media`, {
                headers: {
                    'Authorization': `Bearer ${getToken()}`
                }
            });
            if (!res.ok) throw new Error('Failed to fetch media');
            const data = await res.json();
            setFiles(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const handleUpload = async (e) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        setUploading(true);
        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            const formData = new FormData();
            formData.append('file', file);

            try {
                const res = await fetch(`${API_BASE_URL}/media/upload`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    },
                    body: formData
                });

                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.error || `Failed to upload ${file.name}`);
                }
                toast.success(`Uploaded ${file.name}`);
            } catch (error) {
                toast.error(error.message);
            }
        }

        // Reset input and refresh list
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploading(false);
        setCurrentPage(1); // Reset to first page when uploading new files
        fetchFiles();
    };

    const handleDelete = async (filepath) => {
        if (!window.confirm('Are you sure you want to delete this file? This cannot be undone.')) return;

        try {
            const res = await fetch(`${API_BASE_URL}/media`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify({ filepath })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to delete file');
            }

            toast.success('File deleted successfully');
            setFiles(files.filter(f => f.path !== filepath));
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleCopyLink = (filepath) => {
        // Construct the absolute URL
        // e.g. API_BASE_URL is 'http://localhost:4000/api', we strip '/api'
        const baseUrl = API_BASE_URL.replace(/\/api$/, '');
        const absoluteUrl = `${baseUrl}${filepath}`;

        navigator.clipboard.writeText(absoluteUrl).then(() => {
            toast.success('Link copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    const formatSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const isImage = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
    };

    const totalPages = Math.ceil(files.length / itemsPerPage);
    const displayedFiles = files.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div>


            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <p style={{ color: '#64748b', margin: 0, fontWeight: 500 }}>Manage uploaded images and static documents.</p>
                </div>
                <div>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        onChange={handleUpload}
                        style={{ display: 'none' }}
                    />
                    <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        {uploading ? 'Uploading...' : 'Upload Files'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 20 }}>Loading media files...</div>
            ) : files.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <p style={{ color: '#64748b', marginBottom: 16 }}>No files found in the uploads directory.</p>
                    <button
                        className="admin-btn admin-btn-secondary"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload your first file
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 20,
                    paddingBottom: 20
                }}>
                    {displayedFiles.map((file) => {
                        const baseUrl = API_BASE_URL.replace(/\/api$/, '');
                        const absoluteUrl = `${baseUrl}${file.path}`;

                        return (
                            <div key={file.path} style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                overflow: 'hidden',
                                background: '#fff',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{
                                    height: 150,
                                    background: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderBottom: '1px solid #e2e8f0'
                                }}>
                                    {isImage(file.name) ? (
                                        <img
                                            src={absoluteUrl}
                                            alt={file.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div style={{ color: '#94a3b8' }}>
                                            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                                <polyline points="13 2 13 9 20 9"></polyline>
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        marginBottom: 4,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }} title={file.name}>
                                        {file.name}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                        {formatSize(file.size)} • {new Date(file.createdAt).toLocaleDateString()}
                                    </div>

                                    <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
                                        <button
                                            onClick={() => handleCopyLink(file.path)}
                                            style={{
                                                flex: 1,
                                                padding: '6px 0',
                                                background: '#f1f5f9',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                color: '#334155'
                                            }}
                                        >
                                            Copy Link
                                        </button>
                                        <button
                                            onClick={() => handleDelete(file.path)}
                                            style={{
                                                padding: '6px 12px',
                                                background: '#fee2e2',
                                                border: '1px solid #fca5a5',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                color: '#ef4444'
                                            }}
                                            title="Delete"
                                        >
                                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '20px 0 40px' }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            padding: '8px 16px',
                            background: currentPage === 1 ? '#f1f5f9' : '#fff',
                            color: currentPage === 1 ? '#94a3b8' : '#334155',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontWeight: 500
                        }}
                    >
                        Previous
                    </button>

                    <span style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: '8px 16px',
                            background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                            color: currentPage === totalPages ? '#94a3b8' : '#334155',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontWeight: 500
                        }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
